import { useEffect, useRef, useState } from 'react';
import { PDFDocument } from 'pdf-lib';

export const flattenPdfBytes = async (bytes: Uint8Array): Promise<Uint8Array> => {
  const pdfDoc = await PDFDocument.load(bytes);
  try {
    pdfDoc.getForm().flatten();
  } catch {
    // form may not exist
  }
  return pdfDoc.save();
};

interface UseLivePdfPreviewArgs {
  builder: () => Promise<Uint8Array | null | undefined>;
  deps: unknown[];
  enabled?: boolean;
  delay?: number;
}

export const useLivePdfPreview = ({
  builder,
  deps,
  enabled = true,
  delay = 500,
}: UseLivePdfPreviewArgs): string | null => {
  const [url, setUrl] = useState<string | null>(null);
  const urlRef = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    const handle = window.setTimeout(async () => {
      try {
        const bytes = await builder();
        if (cancelled || !bytes) return;
        const flattened = await flattenPdfBytes(bytes);
        if (cancelled) return;
        const blob = new Blob([flattened as any], { type: 'application/pdf' });
        const nextUrl = URL.createObjectURL(blob);
        if (urlRef.current) URL.revokeObjectURL(urlRef.current);
        urlRef.current = nextUrl;
        setUrl(nextUrl);
      } catch (err) {
        console.error('[livePdfPreview] failed to build preview', err);
      }
    }, delay);
    return () => {
      cancelled = true;
      window.clearTimeout(handle);
    };
  }, [...deps, enabled, delay]);

  useEffect(
    () => () => {
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    },
    [],
  );

  return url;
};
